/**
 * Photo gallery component for box photos.
 */
import React, { useState, useRef } from 'react';
import type { BoxPhoto } from '@shared/types';

interface PhotoGalleryProps {
  photos: BoxPhoto[];
  onUpload: (file: File, caption?: string) => Promise<void>;
  onDelete: (photoId: string) => Promise<void>;
  uploading?: boolean;
}

export function PhotoGallery({ photos, onUpload, onDelete, uploading }: PhotoGalleryProps) {
  const [selectedPhoto, setSelectedPhoto] = useState<BoxPhoto | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    await onUpload(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {/* Upload button */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic"
            onChange={handleFileChange}
            className="hidden"
            id="photo-upload"
            disabled={uploading}
          />
          <label
            htmlFor="photo-upload"
            className={`flex items-center justify-center aspect-square rounded-lg border-2 border-dashed border-border text-text-secondary hover:border-accent hover:text-accent transition-colors cursor-pointer ${
              uploading ? 'opacity-50 pointer-events-none' : ''
            }`}
          >
            {uploading ? (
              <span className="text-sm">Uploading...</span>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <svg
                  className="h-8 w-8"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                <span className="text-sm">Add photo</span>
              </div>
            )}
          </label>
        </div>

        {/* Photo thumbnails */}
        {photos.map((photo) => (
          <div key={photo.id} className="relative group aspect-square">
            <button type="button" onClick={() => setSelectedPhoto(photo)} className="w-full h-full">
              <img
                src={photo.url}
                alt={photo.caption || 'Box photo'}
                className="w-full h-full rounded-lg border border-border object-cover"
              />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(photo.id);
              }}
              className="absolute top-2 right-2 rounded-full bg-black/50 p-1.5 text-white opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Delete photo"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>

      {/* Lightbox modal */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative max-w-4xl max-h-full">
            <img
              src={selectedPhoto.url}
              alt={selectedPhoto.caption || 'Box photo'}
              className="max-w-full max-h-[90vh] rounded-lg"
            />
            <button
              type="button"
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 right-4 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition-colors"
              aria-label="Close"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            {selectedPhoto.caption && (
              <p className="mt-2 text-center text-sm text-white">{selectedPhoto.caption}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
